import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import BlockTimeModal from "../components/BlockTimeModal";
import BookingModal from "../components/BookingModal";
import Toast from "../components/Toast";
import DiaryAgendaView from "../components/diary/DiaryAgendaView";
import DiaryDayView from "../components/diary/DiaryDayView";
import DiaryWeekView from "../components/diary/DiaryWeekView";
import DiaryProviderFilter from "../components/diary/DiaryProviderFilter";
import { StatusFilter, StatusLegend } from "../components/diary/DiaryStatus";
import { STATUS_ORDER } from "../components/diary/diaryShared";
import useDiary from "../hooks/useDiary";
import useUrlState, { param, useScrollRestoration, useValidOption } from "../hooks/useUrlState";
import { apiErrorMessage } from "../services/api";
import { fetchBranches } from "../services/branchService";
import { rescheduleAppointment } from "../services/bookingService";
import { formatDate, formatDateRange, formatMonth, todayIn } from "../services/datetime";

const VIEWS = ["day", "week", "agenda"];

const DIARY_STATE = {
  date: param.date(),
  view: param.oneOf(["day", "week", "agenda"], "day"),
  branch: param.string(),
  // Empty = every status, so a link without it shows the whole diary.
  statuses: param.list(),
  // Empty = every provider. Reset whenever the branch changes.
  providers: param.list(),
};

/**
 * BIZ-12 — the business calendar.
 *
 * Three ways to read the same data. The day view is laid out by resource, which
 * is what reception works from; the week is for load; the agenda is what
 * survives a narrow screen.
 *
 * Everything is drawn on the **branch's** clock, which the API supplies. A
 * chain manager in one country looking at a branch in another sees that
 * branch's day, not their own.
 */
export default function Diary() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const [branches, setBranches] = useState([]);
  // Date, view and branch live in the URL, so Back from an appointment
  // returns to the same day, view and branch.
  const { values, set } = useUrlState(DIARY_STATE, { pageKey: null });
  const branchId = values.branch || branches[0]?.id || "";
  const view = values.view;
  const setView = useCallback((next) => set({ view: next }), [set]);
  // Another branch has other providers: the filter starts over.
  const setBranchId = useCallback((next) => set({ branch: next, providers: [] }), [set]);
  const setProviders = useCallback((next) => set({ providers: next }), [set]);
  const clearProviders = useCallback(() => set({ providers: [] }), [set]);
  const setDate = useCallback((next) => set({ date: next }), [set]);
  useValidOption(values.branch, branches.length ? branches : null, () => set({ branch: "" }));
  const [bookingFor, setBookingFor] = useState(null);
  const [bookingOpen, setBookingOpen] = useState(false);
  const [blockOpen, setBlockOpen] = useState(false);
  const [toast, setToast] = useState(null);

  const diary = useDiary(branchId, view, values.date, setDate, values.providers, clearProviders);
  useScrollRestoration(!diary.loading);

  // Every status is loaded; the filter only decides what is drawn, so
  // switching chips is instant and the counts always describe the whole range.
  // A hand-edited `?statuses=` keeps only the names that exist.
  const statuses = useMemo(
    () => values.statuses.filter((s) => STATUS_ORDER.includes(s)),
    [values.statuses]
  );
  const setStatuses = useCallback((next) => set({ statuses: next }), [set]);

  const visibleAppointments = useMemo(
    () =>
      statuses.length ? diary.appointments.filter((a) => statuses.includes(a.status)) : diary.appointments,
    [diary.appointments, statuses]
  );

  const statusCounts = useMemo(() => {
    if (diary.statusCounts) return diary.statusCounts;
    return diary.appointments.reduce((acc, a) => ({ ...acc, [a.status]: (acc[a.status] ?? 0) + 1 }), {});
  }, [diary.statusCounts, diary.appointments]);

  const hiddenCount = diary.appointments.length - visibleAppointments.length;

  useEffect(() => {
    fetchBranches(1, 100)
      .then(({ branches: list }) => {
        setBranches(list);
        // The diary is useless without a branch, so pick the first one.
      })
      .catch(() => setBranches([]));
  }, [i18n.language]);

  const branch = branches.find((b) => b.id === branchId);

  /**
   * A drag on the grid, or the reschedule dialog, both land here.
   *
   * The move is only ever a request: the API re-checks availability and may
   * refuse, so the diary reloads either way rather than assuming it won.
   */
  const onReschedule = useCallback(
    async ({ appointment, date, time, staffId }) => {
      try {
        await rescheduleAppointment(appointment.id, `${date} ${time}`, staffId);
        setToast({ type: "success", message: t("appointments.rescheduled") });
      } catch (err) {
        setToast({
          type: "error",
          message:
            err.response?.status === 409
              ? t("appointments.slotTaken")
              : apiErrorMessage(err, t("appointments.rescheduleFailed")),
        });
      } finally {
        diary.reload();
      }
    },
    [diary, t]
  );

  const openBooking = (initial = null) => {
    setBookingFor(initial);
    setBookingOpen(true);
  };

  const heading = () => {
    if (!diary.date) return "";

    if (view === "week" && diary.range?.days) {
      const [first] = diary.range.days;
      const last = diary.range.days[6];
      return formatDateRange(first, last, i18n.language);
    }

    if (view === "agenda") return formatMonth(diary.date, i18n.language);

    return formatDate(diary.date, i18n.language, { weekday: "long", month: "long" });
  };

  const isToday = diary.date === todayIn(diary.timezone);

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold">{t("diary.title")}</h1>
          <p className="text-sm text-ink-2">
            {heading()}
            {diary.timezone !== "UTC" && (
              <span className="ms-2 text-xs text-ink-2">({diary.timezone})</span>
            )}
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            className="btn-secondary"
            onClick={() => setBlockOpen(true)}
            disabled={!branchId}
          >
            {t("diary.blockTime")}
          </button>
          <button
            type="button"
            className="btn-primary"
            onClick={() => openBooking(diary.date ? { date: diary.date } : null)}
            disabled={!branchId}
          >
            {t("diary.newBooking")}
          </button>
        </div>
      </div>

      {/* Toolbar */}
      <div className="card mb-4 flex flex-wrap items-end gap-3 p-4">
        <div className="min-w-[180px]">
          <label className="field-label" htmlFor="diary-branch">
            {t("diary.branch")}
          </label>
          <select
            id="diary-branch"
            className="field-input"
            value={branchId}
            onChange={(e) => setBranchId(e.target.value)}
          >
            {branches.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </select>
        </div>

        <div className="flex gap-1 rounded-lg bg-raised p-1">
          {VIEWS.map((name) => (
            <button
              key={name}
              type="button"
              onClick={() => setView(name)}
              className={`rounded-md px-3 py-1 text-sm ${
                view === name ? "bg-surface font-medium shadow-sm" : "text-ink-2"
              }`}
            >
              {t(`diary.views.${name}`)}
            </button>
          ))}
        </div>

        <div className="flex gap-1">
          <button type="button" className="btn-secondary" onClick={() => diary.step(-1)}>
            ‹
          </button>
          <button
            type="button"
            className={`btn-secondary ${isToday ? "font-semibold text-accent-text" : ""}`}
            onClick={diary.goToday}
          >
            {t("diary.today")}
          </button>
          <button type="button" className="btn-secondary" onClick={() => diary.step(1)}>
            ›
          </button>
        </div>

        <div>
          <label className="field-label" htmlFor="diary-date">
            {t("diary.jumpTo")}
          </label>
          <input
            id="diary-date"
            type="date"
            className="field-input"
            value={diary.date ?? ""}
            onChange={(e) => diary.setDate(e.target.value)}
          />
        </div>
      </div>

      {branchId && (
        <div className="card mb-4 space-y-2 p-3 sm:p-4">
          <DiaryProviderFilter
            options={diary.providerOptions}
            selected={values.providers}
            onChange={setProviders}
          />
          <StatusFilter selected={statuses} counts={statusCounts} onChange={setStatuses} />
          {hiddenCount > 0 && !diary.loading && (
            <p className="text-caption text-ink-2" role="status">
              {t("diary.hiddenByFilter", { count: hiddenCount })}{" "}
              <button
                type="button"
                className="font-medium text-accent-text underline underline-offset-2"
                onClick={() => setStatuses([])}
              >
                {t("diary.showAll")}
              </button>
            </p>
          )}
        </div>
      )}

      {diary.error && (
        <div className="mb-4 rounded-lg border border-danger-line bg-danger-wash px-3 py-2 text-sm text-danger-text">
          {diary.error}
        </div>
      )}

      {!branchId ? (
        <div className="card p-10 text-center text-sm text-ink-2">{t("diary.noBranches")}</div>
      ) : diary.loading || !diary.date ? (
        <div className="card p-10 text-center text-sm text-ink-2">{t("common.loading")}</div>
      ) : view === "day" ? (
        <DiaryDayView
          date={diary.date}
          timezone={diary.timezone}
          providers={diary.providers}
          appointments={visibleAppointments}
          blocks={diary.blocks}
          workingHours={diary.workingHours}
          onSelect={(a) => navigate(`/appointments/${a.id}`)}
          onReschedule={onReschedule}
          onEmptyClick={openBooking}
        />
      ) : view === "week" ? (
        <DiaryWeekView
          days={diary.range?.days ?? []}
          timezone={diary.timezone}
          appointments={visibleAppointments}
          blocks={diary.blocks}
          workingHours={diary.workingHours}
          onSelect={(a) => navigate(`/appointments/${a.id}`)}
          onEmptyClick={openBooking}
        />
      ) : (
        <DiaryAgendaView
          timezone={diary.timezone}
          appointments={visibleAppointments}
          onSelect={(a) => navigate(`/appointments/${a.id}`)}
        />
      )}

      <div className="mt-4">
        <StatusLegend />
      </div>

      <BookingModal
        open={bookingOpen}
        branchId={branchId}
        branchName={branch?.name}
        providers={diary.providerOptions}
        initial={bookingFor}
        onClose={() => setBookingOpen(false)}
        onBooked={(appointment) => {
          setToast({
            type: "success",
            message: t("appointments.booked", { reference: appointment.reference }),
          });
          diary.reload();
        }}
      />

      <BlockTimeModal
        open={blockOpen}
        branchId={branchId}
        providers={diary.providerOptions}
        date={diary.date}
        onClose={() => setBlockOpen(false)}
        onBlocked={(displaces) => {
          setToast({
            type: displaces.length ? "error" : "success",
            message: displaces.length
              ? t("diary.blockedWithClashes", { count: displaces.length })
              : t("diary.blocked"),
          });
          diary.reload();
        }}
      />

      <Toast toast={toast} onClose={() => setToast(null)} />
    </div>
  );
}
